/**
 * Release Notes Video Play Button Handler
 * 
 * Handles play button click events and video playback state
 * for sa-release-notes-video blocks with center play button enabled.
 */

(function() {
  'use strict';

  /**
   * Initialize video play button handlers
   */
  function initVideoPlayButtons() {
    const videoContainers = document.querySelectorAll('.release-notes-video-container');
    
    videoContainers.forEach((container) => {
      const playButton = container.querySelector('.release-notes-video-play-button');
      const pauseButton = container.querySelector('.release-notes-video-pause-button');
      const videoLink = container.querySelector('.lazy-video-link');
      const videoElement = container.querySelector('video.lazy-video-item');
      
      // No custom play button: click container (cover/video) to play/pause
      if (!playButton) {
        if (videoElement) {
          container.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (videoElement.paused) {
              videoElement.play().then(() => container.classList.add('is-playing')).catch(() => {});
            } else {
              videoElement.pause();
              container.classList.remove('is-playing');
            }
          });
          videoElement.addEventListener('play', () => container.classList.add('is-playing'));
          videoElement.addEventListener('pause', () => container.classList.remove('is-playing'));
          videoElement.addEventListener('ended', () => container.classList.remove('is-playing'));
          return;
        }
        if (videoLink) {
          // YouTube/Vimeo without custom play button: use native controls only (theme.js loads iframe on scroll)
          const muteObserver = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
              mutation.addedNodes.forEach(function(node) {
                if (node.tagName === 'IFRAME') {
                  const videoType = videoLink.getAttribute('data-video-type');
                  let src = node.src;
                  videoLink.classList.add('has-iframe');
                  if (videoType === 'youtube') {
                    src = src.replace(/[?&]mute=1/, '').replace(/[?&]mute=0/, '');
                    if (!src.includes('enablejsapi=1')) {
                      src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
                    }
                    if (src.includes('?')) {
                      src += (src.includes('mute=') ? '' : '&mute=0');
                    } else {
                      src += '?mute=0';
                    }
                    node.src = src;
                    if (typeof YT === 'undefined') {
                      loadYouTubeAPI(() => {
                        initExternalVideoPlayer(container, videoLink, false);
                      });
                    } else {
                      initExternalVideoPlayer(container, videoLink, false);
                    }
                  } else if (videoType === 'vimeo') {
                    src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
                    if (src.includes('?')) {
                      src += (src.includes('muted=') ? '' : '&muted=0');
                    } else {
                      src += '?muted=0';
                    }
                    node.src = src;
                    initExternalVideoPlayer(container, videoLink, false);
                  }
                  muteObserver.disconnect();
                }
              });
            });
          });
          muteObserver.observe(videoLink, { childList: true, subtree: true });
          const existingIframe = videoLink.querySelector('iframe');
          if (existingIframe) {
            videoLink.classList.add('has-iframe');
            const videoType = videoLink.getAttribute('data-video-type');
            let src = existingIframe.src;
            if (videoType === 'youtube') {
              src = src.replace(/[?&]mute=1/, '').replace(/[?&]mute=0/, '');
              if (!src.includes('enablejsapi=1')) {
                src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
              }
              if (src.includes('?')) {
                src += (src.includes('mute=') ? '' : '&mute=0');
              } else {
                src += '?mute=0';
              }
              existingIframe.src = src;
              if (typeof YT === 'undefined') {
                loadYouTubeAPI(() => {
                  initExternalVideoPlayer(container, videoLink, false);
                });
              } else {
                initExternalVideoPlayer(container, videoLink, false);
              }
            } else if (videoType === 'vimeo') {
              src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
              if (src.includes('?')) {
                src += (src.includes('muted=') ? '' : '&muted=0');
              } else {
                src += '?muted=0';
              }
              existingIframe.src = src;
              initExternalVideoPlayer(container, videoLink, false);
            }
          }
          return;
        }
        return;
      }
      
      let youtubePlayer = null;
      let vimeoPlayer = null;
      
      // Handle play button click
      playButton.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        if (videoElement) {
          // Local video
          videoElement.play().then(() => {
            container.classList.add('is-playing');
          }).catch((error) => {
            console.warn('Video play failed:', error);
          });
        } else if (videoLink) {
          // YouTube/Vimeo - check if iframe already exists
          const existingIframe = videoLink.querySelector('iframe');
          if (existingIframe) {
            // Iframe already exists, use player API to play
            const videoType = videoLink.getAttribute('data-video-type');
            
            // Ensure player API is initialized
            if (!container._youtubePlayer && !container._vimeoPlayer) {
              initExternalVideoPlayer(container, videoLink);
            }
            
            // First, update iframe URL to remove mute=1 and add mute=0
            if (videoType === 'youtube') {
              let src = existingIframe.src;
              const hasMute1 = src.includes('mute=1');
              src = src.replace(/[?&]mute=1/, '').replace(/[?&]mute=0/, '');
              if (!src.includes('enablejsapi=1')) {
                src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
              }
              if (src.includes('?')) {
                src += (src.includes('mute=') ? '' : '&mute=0');
              } else {
                src += '?mute=0';
              }
              // Only update src if it changed (to avoid reloading)
              if (hasMute1 || existingIframe.src !== src) {
                existingIframe.src = src;
              }
            } else if (videoType === 'vimeo') {
              let src = existingIframe.src;
              const hasMuted1 = src.includes('muted=1');
              src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
              if (src.includes('?')) {
                src += (src.includes('muted=') ? '' : '&muted=0');
              } else {
                src += '?muted=0';
              }
              if (hasMuted1 || existingIframe.src !== src) {
                existingIframe.src = src;
              }
            }
            
            // Wait a bit for player to initialize, then play
            setTimeout(() => {
              if (videoType === 'youtube' && container._youtubePlayer) {
                try {
                  // Remove mute and play
                  container._youtubePlayer.unMute();
                  container._youtubePlayer.playVideo();
                  container.classList.add('is-playing');
                } catch (e) {
                  console.warn('YouTube play failed:', e);
                  container.classList.add('is-playing');
                }
              } else if (videoType === 'vimeo' && container._vimeoPlayer) {
                try {
                  container._vimeoPlayer.setVolume(1);
                  container._vimeoPlayer.play();
                  container.classList.add('is-playing');
                } catch (e) {
                  console.warn('Vimeo play failed:', e);
                  // Fallback: update iframe src
                  let src = existingIframe.src;
                  src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
                  if (src.includes('?')) {
                    src += (src.includes('muted=') ? '' : '&muted=0');
                  } else {
                    src += '?muted=0';
                  }
                  existingIframe.src = src;
                  container.classList.add('is-playing');
                }
              } else {
                // Player not initialized, try to update src
                if (videoType === 'youtube') {
                  let src = existingIframe.src;
                  src = src.replace(/[?&]mute=1/, '').replace(/[?&]mute=0/, '');
                  if (!src.includes('enablejsapi=1')) {
                    src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
                  }
                  if (src.includes('?')) {
                    src += (src.includes('mute=') ? '' : '&mute=0');
                  } else {
                    src += '?mute=0';
                  }
                  existingIframe.src = src;
                } else if (videoType === 'vimeo') {
                  let src = existingIframe.src;
                  src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
                  if (src.includes('?')) {
                    src += (src.includes('muted=') ? '' : '&muted=0');
                  } else {
                    src += '?muted=0';
                  }
                  existingIframe.src = src;
                }
                container.classList.add('is-playing');
              }
            }, 100);
          } else {
            // Iframe doesn't exist yet, trigger click and watch for iframe creation
            videoLink.click();
            // Watch for iframe creation and remove mute parameter
            const muteObserver = new MutationObserver(function(mutations) {
              mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                  if (node.tagName === 'IFRAME') {
                    const videoType = videoLink.getAttribute('data-video-type');
                    let src = node.src;
                    
                    if (videoType === 'youtube') {
                      // Remove mute=1 and set mute=0, ensure enablejsapi=1
                      src = src.replace(/[?&]mute=1/, '').replace(/[?&]mute=0/, '');
                      if (!src.includes('enablejsapi=1')) {
                        src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
                      }
                      if (src.includes('?')) {
                        src += (src.includes('mute=') ? '' : '&mute=0');
                      } else {
                        src += '?mute=0';
                      }
                      // Update src immediately to prevent muted playback
                      node.src = src;
                    } else if (videoType === 'vimeo') {
                      // Remove muted=1 and set muted=0
                      src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
                      if (src.includes('?')) {
                        src += (src.includes('muted=') ? '' : '&muted=0');
                      } else {
                        src += '?muted=0';
                      }
                      // Update src immediately to prevent muted playback
                      node.src = src;
                    }
                    
                    container.classList.add('is-playing');
                    
                    // Load YouTube API if needed, then initialize player
                    if (videoType === 'youtube' && typeof YT === 'undefined') {
                      loadYouTubeAPI(() => {
                        initExternalVideoPlayer(container, videoLink, true); // Pass true to auto-play
                      });
                    } else {
                      initExternalVideoPlayer(container, videoLink, true); // Pass true to auto-play
                    }
                    
                    muteObserver.disconnect();
                  }
                });
              });
            });
            
            muteObserver.observe(videoLink, {
              childList: true,
              subtree: true
            });
            
            // Fallback timeout
            setTimeout(() => {
              muteObserver.disconnect();
              const iframe = videoLink.querySelector('iframe');
              if (iframe) {
                const videoType = videoLink.getAttribute('data-video-type');
                let src = iframe.src;
                
                if (videoType === 'youtube') {
                  src = src.replace(/[?&]mute=1/, '').replace(/[?&]mute=0/, '');
                  if (!src.includes('enablejsapi=1')) {
                    src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
                  }
                  if (src.includes('?')) {
                    src += (src.includes('mute=') ? '' : '&mute=0');
                  } else {
                    src += '?mute=0';
                  }
                  iframe.src = src;
                } else if (videoType === 'vimeo') {
                  src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
                  if (src.includes('?')) {
                    src += (src.includes('muted=') ? '' : '&muted=0');
                  } else {
                    src += '?muted=0';
                  }
                  iframe.src = src;
                }
                
                container.classList.add('is-playing');
                
                // Load YouTube API if needed, then initialize player
                if (videoType === 'youtube' && typeof YT === 'undefined') {
                  loadYouTubeAPI(() => {
                    initExternalVideoPlayer(container, videoLink, true); // Pass true to auto-play
                  });
                } else {
                  initExternalVideoPlayer(container, videoLink, true); // Pass true to auto-play
                }
              }
            }, 500);
          }
        }
      });
      
      // Handle pause button click
      if (pauseButton) {
        pauseButton.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          if (videoElement) {
            // Local video
            videoElement.pause();
            container.classList.remove('is-playing');
          } else if (videoLink) {
            // YouTube/Vimeo - pause via API
            pauseExternalVideo(container, videoLink);
          }
        });
      }
      
      // Handle local video playback events
      if (videoElement) {
        videoElement.addEventListener('play', function() {
          container.classList.add('is-playing');
        });
        
        videoElement.addEventListener('pause', function() {
          container.classList.remove('is-playing');
        });
        
        videoElement.addEventListener('ended', function() {
          container.classList.remove('is-playing');
        });
      }
      
      // Handle YouTube/Vimeo iframe load (for auto-loading videos)
      if (videoLink) {
        // Watch for iframe creation (YouTube/Vimeo lazy load)
        const observer = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            mutation.addedNodes.forEach(function(node) {
              if (node.tagName === 'IFRAME') {
                // Remove mute parameter for auto-loaded videos
                const videoType = videoLink.getAttribute('data-video-type');
                let src = node.src;
                
                if (videoType === 'youtube') {
                  src = src.replace(/[?&]mute=1/, '').replace(/[?&]mute=0/, '');
                  if (!src.includes('enablejsapi=1')) {
                    src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
                  }
                  if (src.includes('?')) {
                    src += (src.includes('mute=') ? '' : '&mute=0');
                  } else {
                    src += '?mute=0';
                  }
                  // Update src immediately to prevent muted playback
                  node.src = src;
                } else if (videoType === 'vimeo') {
                  src = src.replace(/[?&]muted=1/, '').replace(/[?&]muted=0/, '');
                  if (src.includes('?')) {
                    src += (src.includes('muted=') ? '' : '&muted=0');
                  } else {
                    src += '?muted=0';
                  }
                  // Update src immediately to prevent muted playback
                  node.src = src;
                }
                
                container.classList.add('is-playing');
                
                // Load YouTube API if needed, then initialize player
                if (videoType === 'youtube' && typeof YT === 'undefined') {
                  loadYouTubeAPI(() => {
                    initExternalVideoPlayer(container, videoLink);
                  });
                } else {
                  initExternalVideoPlayer(container, videoLink);
                }
                
                observer.disconnect();
              }
            });
          });
        });
        
        observer.observe(videoLink, {
          childList: true,
          subtree: true
        });
      }
    });
  }

  /**
   * Load YouTube IFrame Player API
   */
  function loadYouTubeAPI(callback) {
    if (typeof YT !== 'undefined' && YT.Player) {
      if (callback) callback();
      return;
    }
    
    // Check if script is already loading
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      // Wait for YT to be available
      const checkYT = setInterval(() => {
        if (typeof YT !== 'undefined' && YT.Player) {
          clearInterval(checkYT);
          if (callback) callback();
        }
      }, 100);
      return;
    }
    
    // Load YouTube API script
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    
    // Wait for YT to be available
    const checkYT = setInterval(() => {
      if (typeof YT !== 'undefined' && YT.Player) {
        clearInterval(checkYT);
        if (callback) callback();
      }
    }, 100);
    
    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkYT);
      if (callback) callback();
    }, 5000);
  }

  /**
   * Initialize external video player (YouTube/Vimeo) API
   */
  function initExternalVideoPlayer(container, videoLink, autoPlay) {
    const iframe = videoLink.querySelector('iframe');
    if (!iframe) return;
    
    const videoType = videoLink.getAttribute('data-video-type');
    const videoId = videoLink.getAttribute('data-video-id');
    
    if (videoType === 'youtube') {
      // YouTube API
      if (typeof YT !== 'undefined' && YT.Player) {
        try {
          // Don't reinitialize if player already exists
          if (container._youtubePlayer) {
            // If autoPlay is requested, unmute and play
            if (autoPlay) {
              try {
                container._youtubePlayer.unMute();
                container._youtubePlayer.playVideo();
              } catch (e) {
                console.warn('YouTube auto-play failed:', e);
              }
            }
            return;
          }
          
          const player = new YT.Player(iframe, {
            events: {
              onReady: function(event) {
                // Player is ready - unmute and play if autoPlay is requested
                if (autoPlay) {
                  try {
                    event.target.unMute();
                    event.target.playVideo();
                  } catch (e) {
                    console.warn('YouTube auto-play on ready failed:', e);
                  }
                }
              },
              onStateChange: function(event) {
                if (event.data === YT.PlayerState.PLAYING) {
                  container.classList.add('is-playing');
                  // Ensure unmuted when playing
                  try {
                    event.target.unMute();
                  } catch (e) {
                    // Ignore errors
                  }
                } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
                  container.classList.remove('is-playing');
                }
              }
            }
          });
          container._youtubePlayer = player;
        } catch (e) {
          console.warn('YouTube player initialization failed:', e);
        }
      }
    } else if (videoType === 'vimeo') {
      // Vimeo API
      if (typeof Vimeo !== 'undefined' && Vimeo.Player) {
        try {
          // Don't reinitialize if player already exists
          if (container._vimeoPlayer) {
            if (autoPlay) {
              try {
                container._vimeoPlayer.setVolume(1);
                container._vimeoPlayer.play();
                container.classList.add('is-playing');
              } catch (e) {
                console.warn('Vimeo auto-play failed:', e);
              }
            }
            return;
          }
          
          const player = new Vimeo.Player(iframe);
          player.on('play', function() {
            container.classList.add('is-playing');
          });
          player.on('pause', function() {
            container.classList.remove('is-playing');
          });
          player.on('ended', function() {
            container.classList.remove('is-playing');
          });
          container._vimeoPlayer = player;
          if (autoPlay) {
            try {
              player.setVolume(1);
              player.play();
              container.classList.add('is-playing');
            } catch (e) {
              console.warn('Vimeo auto-play failed:', e);
            }
          }
        } catch (e) {
          console.warn('Vimeo player initialization failed:', e);
        }
      }
    }
  }

  /**
   * Pause external video (YouTube/Vimeo)
   */
  function pauseExternalVideo(container, videoLink) {
    const videoType = videoLink.getAttribute('data-video-type');
    
    if (videoType === 'youtube') {
      if (container._youtubePlayer) {
        try {
          container._youtubePlayer.pauseVideo();
          container.classList.remove('is-playing');
        } catch (e) {
          console.warn('YouTube pause failed:', e);
          // Fallback: send postMessage to iframe
          const iframe = videoLink.querySelector('iframe');
          if (iframe && iframe.contentWindow) {
            try {
              iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
              container.classList.remove('is-playing');
            } catch (e2) {
              console.warn('YouTube postMessage pause failed:', e2);
            }
          }
        }
      } else {
        // Player not initialized, try postMessage
        const iframe = videoLink.querySelector('iframe');
        if (iframe && iframe.contentWindow) {
          try {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            container.classList.remove('is-playing');
          } catch (e) {
            console.warn('YouTube postMessage pause failed:', e);
          }
        }
      }
    } else if (videoType === 'vimeo') {
      if (container._vimeoPlayer) {
        try {
          container._vimeoPlayer.pause();
          container.classList.remove('is-playing');
        } catch (e) {
          console.warn('Vimeo pause failed:', e);
        }
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoPlayButtons);
  } else {
    initVideoPlayButtons();
  }
  
  // Re-initialize for dynamically loaded content
  if (typeof window.initReleaseNotesVideo === 'undefined') {
    window.initReleaseNotesVideo = initVideoPlayButtons;
  }
})();

